import { Box } from "@mui/material";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { ModalStatus, type UseModalState } from "@src/lib/ui-react";
import { useMemo } from "react";

import { NotificationPreferencesList } from "./NotificationPreferencesList";
import { type NotificationChangeArguments, type NotificationRow } from "./types";
import { useTu } from "@src/appV2/i18n/useTu";

export interface NotificationPreferencesDialogProps {
  isOpen: boolean;
  rows: NotificationRow[];
  smsDisabledNoPhone: boolean;
  onChangeNotification: (arguments_: NotificationChangeArguments) => void;
  onSelectorOpen: () => void;
  onClose: () => void;
  onDone: () => void;
}

interface DialogHeaderRowProps {
  title: string;
  onClose: () => void;
}

function DialogHeaderRow(props: DialogHeaderRowProps) {
  const { tu } = useTu("workplaceUsers");
  const { title, onClose } = props;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(5),
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.border?.base}`,
      })}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text bold sx={{ whiteSpace: "normal" }}>
          {title}
        </Text>
      </Box>
      <IconButton
        aria-label={tu("Close dialog")}
        iconType="close"
        size="medium"
        variant="outlined"
        onClick={onClose}
      />
    </Box>
  );
}

export function NotificationPreferencesDialog(props: NotificationPreferencesDialogProps) {
  const { tu } = useTu("workplaceUsers");
  const {
    isOpen,
    rows,
    smsDisabledNoPhone,
    onChangeNotification,
    onSelectorOpen,
    onClose,
    onDone,
  } = props;

  const modalState: UseModalState = useMemo(
    () => ({
      modalStatus: isOpen ? ModalStatus.OPEN : ModalStatus.CLOSED,
      modalIsOpen: isOpen,
      modalIsClosed: !isOpen,
      setModalStatus: () => undefined,
      openModal: () => undefined,
      closeModal: () => undefined,
    }),
    [isOpen]
  );

  return (
    <BerlinThemeProvider>
      <Dialog
        modalState={modalState}
        width={800}
        headerContent={<DialogHeaderRow title={tu("Notification Preferences")} onClose={onClose} />}
        footerContent={<DialogFooter primaryAction={{ label: "Done", onClick: onDone }} />}
        onClose={onClose}
      >
        <NotificationPreferencesList
          rows={rows}
          smsDisabledNoPhone={smsDisabledNoPhone}
          onChangeNotification={onChangeNotification}
          onSelectorOpen={onSelectorOpen}
        />
      </Dialog>
    </BerlinThemeProvider>
  );
}
